// The SPA's one way of talking to @cca/decides-worker.
//
// ── THE TRANSPORT IS A HEADER TOKEN, AND THAT IS FORCED, NOT PREFERRED ──────
// `@cca/session`'s cookie wrapper fixes `SameSite=Lax` and says so in terms
// (packages/cca-session/src/cookie.ts). This SPA is a Pages deployment on its
// OWN ORIGIN and the worker is on another, so a Lax cookie would simply never be
// sent on these requests — the session would appear to vanish on every call. The
// estate settled this already: the Finance Portal carries its session in an
// `X-Session-Token` request header, and `@cca/worker-app`'s ESTATE_ALLOW_HEADERS
// already lists that header, so the envelope's CORS admits it with no change.
// This module is that scheme, not a third one.
//
// ── WHAT THE TOKEN IS, SAID PLAINLY ────────────────────────────────────────
// A BEARER credential in browser storage. Anybody holding it is the person it
// was minted for, until its rolling idle life lapses or somebody bumps the
// session epoch. `localStorage` is the estate's existing choice for this and is
// kept for consistency; it is readable by any script that runs on this origin,
// so the protection against theft is that no third-party script runs here, not
// anything this file does. Stated because it is the limit a reader should know,
// not because it is new.
//
// ── AND WHAT A TOKEN IS *NOT* ──────────────────────────────────────────────
// It is not permission to see anything. Every restricted surface re-resolves
// what the holder may do from the grant register on the request that asks. This
// client can therefore be handed a perfectly valid token and still be refused —
// which is the ordinary case, not an error, and `DecidesApiError` below exists so
// the screen can say so in words rather than showing a wire code.

import { reportTelemetry } from '@cca/ui';
import type { CountResult } from '@cca/decides-count';
import type { MotionMark, PaperCandidate, PaperMark } from '../paper';

// THE ONE ENGINE IMPORT HERE IS `CountResult` (type-only), AND IT IS THE RESULTS
// SHEET'S OWN CONTRACT RATHER THAN AN ASSERTION ABOUT A PLAIN COLUMN. The count
// route returns the engine's structured aggregate verbatim, and ../results/view.ts
// already types `ResultView.result` as exactly this — so naming it here is the same
// contract, not a second one, and a rule that moves in the engine moves through both.
//
// WHAT IS STILL DELIBERATELY NOT IMPORTED is the engine's closed UNIONS for the
// plain `TEXT` columns the wire also carries. This module used to declare `mode`,
// `electorate`, `method` and `house` as those unions, which made a plain column
// arriving off the network look, to the compiler, like a value somebody had checked.
// Nobody had: this file's types are ASSERTIONS about a payload, not validations of
// one, distinguishable only at runtime. The wire's words are the DATABASE's words
// (`mode`, `electorate`, `method`, `secrecy` and `voter_house` each carry a CHECK):
// they stay `string` on the wire — see `CountedSheet.mode`/`secrecy` below — and are
// narrowed once, at the boundary that reads them (../paper's `asBallotMode`, and the
// scrutineer panel for secrecy), where a word this build cannot name has somewhere
// honest to go.

/** Where the worker lives. Empty in a same-origin dev proxy; a full origin in
 *  staging/production, where the SPA is on Pages and the worker is not. */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/** localStorage key. Namespaced per portal — a shared key across portals would
 *  mean one sign-out ending sessions the person did not ask to end. */
const TOKEN_KEY = 'decides_session_token';

/** Fired when the worker rejects the stored token, so the shell can drop back to
 *  the door without every caller having to handle it. */
export const SESSION_EXPIRED_EVENT = 'decides:session-expired';

export function sessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function storeSessionToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * A REFUSAL BODY EXACTLY AS IT ARRIVED — every key, no interpretation.
 *
 * `unknown`-valued and readonly, and both halves are deliberate. The worker sends
 * a DIFFERENT set of figures per code — the standing close on `not_an_extension`,
 * the cap and the latest acceptable instant on `extension_too_far`, the shape and
 * an example on `extend_invalid` — and this module knows none of them, so nothing
 * here may be typed as though it did. Every consumer NARROWS a value before it
 * reads one (`typeof x === 'string'`), which is the same discipline `state` above
 * records: a figure off this wire is evidence, not a type.
 */
export type RefusalDetails = Readonly<Record<string, unknown>>;

/** The empty body, shared and frozen — so `details` is never `undefined` and no caller
 *  needs a null check in front of a lookup. FROZEN because every refusal that carried
 *  nothing hands back this same object: one stray write would reach every other
 *  refusal on the screen, and the readonly type only stops that at compile time. The
 *  type argument is explicit so the assignment does not rest on TypeScript inferring an
 *  index signature for an empty literal through `Readonly`. */
export const NO_REFUSAL_DETAILS: RefusalDetails = Object.freeze<RefusalDetails>({});

/**
 * A refusal the worker made deliberately, carrying its wire code.
 *
 * The code is kept so the SCREEN can choose a sentence — `no_grant` and an
 * expired session are different things to be told, and the README records that
 * turning these four codes into human sentences is owed by this stream. It is
 * deliberately not turned into a message here: this module has no idea which
 * screen is asking or what it was trying to do.
 */
export class DecidesApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /**
     * THE BALLOT STATE A REFUSAL CARRIED BESIDE ITS CODE, and `null` on every
     * refusal that carried none — which is all of them but one.
     *
     * `GET /api/results/:ballotId` answers `409 { error: 'ballot_not_counted',
     * state }` for a ballot that has not been closed (GATE 4,
     * `@cca/decides-worker/src/routes/results.ts`), and the three scrutineer acts
     * answer `409 { error: 'wrong_state', state }` the same way. That word is the
     * only cheap read of a ballot's TRUE state this SPA has, and a panel that
     * discards it has to assume one instead — which is exactly the defect
     * `../scrutineer/ScrutineerPage.tsx` was carrying. So it is surfaced here
     * rather than thrown away with the rest of the body.
     *
     * It is a plain `string`, like every other CHECK-fenced column off this wire,
     * and is narrowed by the surface that reads it — never asserted here.
     */
    readonly state: string | null = null,
    /**
     * THE WHOLE REFUSAL BODY, KEPT RATHER THAN DISCARDED — see
     * {@link RefusalDetails}.
     *
     * **The extend route deliberately sends the figures a repair needs beside its
     * word** (`@cca/decides-worker/src/routes/setup.ts`: the close that stands, the
     * latest instant that would be accepted, the cap in hours, the shape and an
     * example) — precisely because a registrar standing in a hall told only
     * `not_an_extension` does not know what the current close IS and can only guess
     * again. Everything but `error` and `state` used to be dropped one layer above
     * the wire, here, which destroyed every one of them.
     *
     * `code` and `state` are STILL lifted out separately and nothing about them has
     * changed: they are the two values every surface reads, and folding them into a
     * lookup would make the common case narrow for no gain. This is the REST, and it
     * defaults to the shared empty body so no existing call site sees a difference.
     */
    readonly details: RefusalDetails = NO_REFUSAL_DETAILS,
  ) {
    super(`${status} ${code}`);
    this.name = 'DecidesApiError';
  }
}

/** The worker's refusal bodies are all `{ error: '<code>' }`, some carry a `state`
 *  beside it (see {@link DecidesApiError.state}) and some carry the figures a
 *  repair needs (see {@link DecidesApiError.details}). Anything that is not a JSON
 *  OBJECT — an HTML error page from an edge, an empty body, a bare array — reads as
 *  `unknown` with no details, never as success and never as a state: an array would
 *  otherwise pass a plain `typeof === 'object'` test and reach a consumer as a
 *  record whose every lookup is `undefined`. */
async function refusal(
  res: Response,
): Promise<{ code: string; state: string | null; details: RefusalDetails }> {
  try {
    const body = (await res.json()) as unknown;
    if (typeof body !== 'object' || body === null || Array.isArray(body)) {
      return { code: 'unknown', state: null, details: NO_REFUSAL_DETAILS };
    }
    const details = body as RefusalDetails;
    return {
      code: typeof details.error === 'string' ? details.error : 'unknown',
      state: typeof details.state === 'string' ? details.state : null,
      details,
    };
  } catch {
    return { code: 'unknown', state: null, details: NO_REFUSAL_DETAILS };
  }
}

/**
 * One request to the worker, with the session header attached when there is one.
 *
 * `X-Requested-With` is not decoration: the estate envelope refuses every
 * mutating /api/* request that does not carry exactly that header (its CSRF
 * fence). A `<form>` cannot set it and a cross-origin fetch trips the refused
 * preflight first, which is what makes it work.
 *
 * A 401 clears the token and announces it, because a token the server will not
 * accept is worse than no token: kept, it makes every later call fail the same
 * way, and the person is never shown the door. Telemetry deliberately does NOT
 * fire on 401 or 403 — an expired session and a withdrawn grant are expected,
 * handled states, and reporting them as errors would bury the real ones.
 */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = sessionToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(token !== null ? { 'X-Session-Token': token } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    reportTelemetry('api_error', `Network failure on ${path}`, path);
    throw err;
  }

  if (res.status === 401) {
    clearSessionToken();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    const { code, state, details } = await refusal(res);
    throw new DecidesApiError(401, code, state, details);
  }
  if (res.status === 403) {
    // A real, deliberate refusal by the grant register. The session stays: it is
    // valid and belongs to a real person, and a restored grant should not cost
    // them a fresh sign-in.
    const { code, state, details } = await refusal(res);
    throw new DecidesApiError(403, code, state, details);
  }
  if (!res.ok) {
    const { code, state, details } = await refusal(res);
    reportTelemetry('api_error', `API ${res.status} ${code} on ${path}`, path);
    throw new DecidesApiError(res.status, code, state, details);
  }
  return res.json() as Promise<T>;
}

/** A sitting this person holds a role in. The role word is the SERVER's word —
 *  the same pair as `role_grant.role`'s CHECK and the worker's `GrantedRole`. */
export interface StandingSitting {
  electionId: string;
  mode: string;
  name: string;
  role: 'admin' | 'scrutineer';
}

/**
 * A sitting this person may VOTE in.
 *
 * **A DIFFERENT REGISTER FROM {@link StandingSitting}, and that is the whole
 * point.** Standing to vote comes from the frozen ROLL — `contact_email` joined
 * to `effective_voter` — and never from `role_grant` (registrar ruling D2,
 * 2026-08-19). So it carries no role word: there is no role to carry. A member
 * of Synod runs nothing and watches nothing; they hold a paper.
 */
export interface ElectorSitting {
  electionId: string;
  mode: string;
  name: string;
}

/**
 * `GET /api/auth/me`. Carries NOTHING NUMERIC, by design — see the route.
 *
 * **`electorSittings` is OPTIONAL on purpose, and it is not laziness.** It is a
 * new field, added by the cast path, and this SPA and the worker deploy
 * separately — a browser holding a cached bundle can meet a worker that predates
 * it, and the reverse. Absent therefore reads as "this server does not answer
 * that question", which is exactly what it means, and the landing shows no
 * ballot section rather than throwing on `undefined.length`.
 *
 * The worker now declares it REQUIRED and always sends it (`SignInStanding`,
 * `@cca/decides-worker/src/auth.ts`), so the absent case is deploy skew and
 * nothing else. It stays optional for exactly that window: the route's three
 * keys are `email`, `sittings` and `electorSittings`, and this is the only one of
 * the three whose absence a member could meet in the ordinary course of a
 * release.
 */
export interface Identity {
  email: string;
  sittings: StandingSitting[];
  electorSittings?: ElectorSitting[];
}

// ── The attendance register ────────────────────────────────────────────────
// The Administrator's screen. Every word of these types is the SERVER's word:
// `presence` is `attendance_event.presence`'s CHECK, `house` is `elector.house`'s,
// and the per-House counts are derived from the effective roll by the route and
// never in this browser.

/** The two Houses plus the Bishop, who is NOT a third House — he is counted on
 *  his own, exactly as `summoned_bishop` is (registrar ruling 2026-08-14). */
export type RegisterHouse = 'clergy' | 'laity' | 'bishop';

/** The asserted STATE of a person, never a change to one. `null` on a
 *  `RegisterElector` is the THIRD state — UNRECORDED — and it is not a value
 *  this type may carry, because it is the absence of any event. */
export type Presence = 'present' | 'absent';

export interface RegisterElector {
  electorId: string;
  /** The Zoho `Contacts.id` — an identifier (data tier 2), and the only thing
   *  this database can say about who a person IS: `elector` has no name column
   *  and no email column, by design. */
  contactId: string | null;
  house: RegisterHouse;
  /** `null` is UNRECORDED, and is kept distinct from `absent` because that is
   *  what separates "the room is thin" from "the register was never finished". */
  presence: Presence | null;
  seq: number | null;
  recordedBy: string | null;
  recordedAtUtc: string | null;
  reason: string | null;
}

/**
 * The derived count for ONE House. **There is no total and there must not be
 * one**: SO 12.1 is a per-House test, and a sum is satisfied by one House
 * carrying the whole roll. `present + absent + unrecorded === onRoll`.
 */
export interface RegisterHouseCount {
  house: RegisterHouse;
  onRoll: number;
  present: number;
  absent: number;
  unrecorded: number;
}

export interface RegisterView {
  sitting: {
    electionId: string;
    mode: 'live' | 'rehearsal' | 'sandbox';
    name: string;
    snapshotId: string;
    /** The published genesis of both chains — the frozen roll's digest. */
    rollDigest: string;
  };
  /** Where the register itself stands. `seq: 0` is an empty register. */
  chain: { seq: number; headHash: string | null };
  electors: RegisterElector[];
  houses: RegisterHouseCount[];
  viewer: 'admin';
}

/** What one appended link recorded. `seq` and `prevHash` are DERIVED by the
 *  worker from the chain head; this client sends neither and could not forge
 *  one if it did. */
export interface RegisterEvent {
  id: string;
  seq: number;
  hash: string;
  prevHash: string;
  occurredAtUtc: string;
  occurredNzDate: string;
}

/** The answer to an append: the link, and the register as at after it. The list
 *  and the counts come back from a fresh server read rather than being adjusted
 *  in this browser — an in-memory increment is a second answer, and it drifts
 *  the moment two Administrators are at the door. */
export interface RegisterAppended {
  event: RegisterEvent;
  electors: RegisterElector[];
  houses: RegisterHouseCount[];
}

// ── The cast path ──────────────────────────────────────────────────────────
// What a MEMBER of Synod gets: the ballots open to them, the paper for one of
// them, and one submission.
//
// ── THESE THREE SHAPES ARE READ OFF THE WORKER, NOT AGREED WITH IT ─────────
// Reconciled field by field on 2026-08-27 against
// `@cca/decides-worker/src/routes/ballot.ts` and `src/cast.ts`, which are the
// contract: they are the server, and every field below is spelt the way that
// code spells it. They were previously ASSUMED — this half and the worker half
// were built in parallel — and both halves type-checked green the whole time,
// because each was checked against its own declarations and nothing in this
// estate types the wire between them. What that bought was three routes that
// would each have failed at runtime, at a synod sitting, on the one screen that
// cannot be retried.
//
// SO THE FIELDS ARE THE WORKER'S, INCLUDING THE ONES NO SCREEN HERE READS.
// A wire type that quietly renames a field is how the next reader learns the
// wrong name, and `electionName` read as `sittingName` renders as nothing at
// all — silently, in the place a member picks which ballot to open.
//
// NOT DECLARED, DELIBERATELY, and each absence is load-bearing:
//  • no tally, no turnout, no participation figure, on ANY of the three. A
//    visible number during an open secret ballot changes votes, and a member
//    never sees one at all (registrar ruling 2026-08-16). The routes send none.
//  • no closing instant and no clock. A countdown in a contested election is a
//    pressure device; the President saying "voting closes in one minute" is the
//    honest instrument.
//  • **no `paperId`, although the issue route sends one.** This is the one
//    field below that is dropped rather than absent, and dropping it is the
//    point: rule 3 at the head of the worker's `src/cast.ts` records that the
//    paper is resolved through `paper_claim` and that "the issue response
//    contains one, so a builder will reach for it". A field this client never
//    declares is a field no screen here can render and no later change can
//    quietly start sending — and on a secret ballot a paper identifier in a
//    browser is a handle with no use in this app.
//  • **no existing marks.** `POST …/paper` is issue-or-RESUME and the route
//    answers `paper.recorded`, a boolean, and no marks at all — deliberately,
//    because the only state in which it COULD return them is after the vote was
//    recorded, where returning them would restate the vote on the screen and in
//    the screen reader. Revision is not built on either side.

/**
 * One ballot a member may vote in, as listed on their landing. Carries the
 * title and nothing that could be read as a figure or a clock.
 */
export interface OpenBallot {
  ballotId: string;
  /** The SITTING this ballot belongs to. Nothing on the list renders it — the
   *  link is by `ballotId` — and it is declared because the route sends it and
   *  because `(election_id, mode)` is the sitting's identity everywhere in this
   *  schema, so a reader here should not have to go and find that out. */
  electionId: string;
  /** `live` | `rehearsal` | `sandbox`, so a rehearsal can never be dressed as a
   *  live vote. A plain string on the wire; narrowed by ../paper's
   *  `asBallotMode` wherever it decides a rendering. */
  mode: string;
  /** The ballot's own title — the question or the office, not the sitting's. */
  title: string;
  /** `motion` | `position` — the ballot is homogeneous, so this is the ballot's
   *  kind and not an item's. Not rendered on the list today; declared because
   *  the route sends it and a wire type that omits half the payload is a
   *  description of the payload that is wrong. */
  itemKind: string;
  /** The sitting's NAME, so a member on two rolls can tell them apart. The
   *  worker's word: `election.name`, sent as `electionName`. */
  electionName: string;
}

/**
 * One item as it appears on THIS elector's paper — `PaperItem` in
 * `@cca/decides-worker/src/cast.ts`, field for field, strings and nulls
 * included.
 *
 * IT IS DELIBERATELY NOT A DISCRIMINATED UNION HERE, although the two kinds are
 * genuinely different things. `kind` is a `TEXT` column
 * (`CHECK (kind IN ('motion', 'position'))`) and the nullability of `electorate`,
 * `seats`, `alternateSeats` and `method` is the SCHEMA's, not a union's: a
 * motion carries nulls in all four. Declaring the union here would mean
 * asserting the correlation rather than testing it, and the place that tests it
 * is cast/CastPage.tsx, which has somewhere to put a paper it cannot read.
 */
export interface CastItem {
  itemId: string;
  kind: string;
  agendaPosition: number;
  title: string;
  /** `clergy` | `laity` | `both` on a position; **null on a motion**. */
  electorate: string | null;
  seats: number | null;
  alternateSeats: number | null;
  method: string | null;
  /**
   * The MOTION's majority base — `present-and-voting` | `summoned-per-house` |
   * `all-synod-members`; **null on a position**, the mirror of `electorate`
   * (`ballot_item_motion_columns_iff_motion` makes it NOT NULL only on a motion).
   *
   * It is the key into `../thresholds`' `THRESHOLD_COPY`, which is the ratified
   * sentence a member reads to know what majority their vote is measured
   * against. **It carries no default, here or on the wire**: a motion whose base
   * this screen cannot read must be REFUSED, never rendered against a guessed
   * one — `present-and-voting` is the lowest of the three constitutional bars, so
   * a substituted value is wrong in the direction that reassures.
   */
  majorityBase: string | null;
  /**
   * THE INSTRUMENT — the words Synod is being asked to agree, verbatim;
   * **null on a position**, alongside `majorityBase`.
   *
   * It is what a motion paper renders in full: never truncated, never behind a
   * "show more", never in a scroll box. A member who cannot read the whole
   * motion cannot consent to it, so a motion whose instrument is absent or
   * empty is REFUSED by the cast screen rather than put to a member as a title
   * with three radio buttons under it.
   *
   * It is prose an operator typed, and it reaches the DOM as the text child of
   * a `<p>` — never as markup. See `instrumentParagraphs` (../paper).
   */
  instrument: string | null;
  /**
   * Who moved the motion, and who seconded it — display names as the record
   * holds them; **null on a position**, and null on a motion whose record does
   * not carry the name.
   *
   * Nothing on this path is an identity: the platform's identity is
   * `Contacts.id` (ADR-059), and these two are captions on a paper.
   */
  mover: string | null;
  seconder: string | null;
  /**
   * SO 15.8, mirrored from the database's own fence
   * (`mark_subject_is_in_the_item_electorate`).
   *
   * **False means RENDER IT UNMARKABLE, never omit it** (registrar ruling
   * 2026-08-20). Every item on the ballot is served to every elector — a paper
   * that was simply SHORT of an item would itself be a discriminator — and an
   * unentitled item earns NO mark row at all when the paper is recorded, which
   * is what keeps a spurious `blank` out of the denominator of an election this
   * House has no part in.
   */
  entitled: boolean;
  candidates: PaperCandidate[];
}

/**
 * `POST /api/ballot/:ballotId/paper` — issue-or-resume, idempotent by claim.
 * A refreshed tab, a flaky hall wifi and a double-tap all land on the same
 * paper.
 *
 * **THE BODY IS NESTED, and the nesting is the worker's.** Three objects that
 * are three different things: the BALLOT (facts about the poll, read from the
 * ballot row and never from a request parameter), the PAPER (facts about this
 * elector's paper), and the ITEMS. This client previously declared one flat
 * object with a `ballotTitle`, a `state` and an `alreadyRecorded`, none of which
 * exist — so every field would have read `undefined`, and `state` reading
 * `undefined` closes the poll on the screen and disables the submit button.
 */
export interface CastPaperView {
  ballot: {
    ballotId: string;
    title: string;
    /** `motion` | `position`. */
    itemKind: string;
    /** `live` | `rehearsal` | `sandbox` — see {@link OpenBallot.mode}. */
    mode: string;
    /**
     * `secret` | `attributable` — the ballot row's own value, so a rehearsal
     * cannot be made to look live and a secret ballot cannot be made to look
     * published by any caller.
     *
     * **It is not decoration and it is not for the operator.** On an
     * attributable ballot the member's vote is recorded against their name and
     * published with the result (`paper_elector_contact_id_iff_attributable`),
     * and they are told so ABOVE the rows, before they mark. The absence of a
     * band is the secret case, which is what this whole surface is built for.
     */
    secrecy: string;
    /**
     * `one-tally` | `separate-chambers` — how this ballot's marks aggregate. The
     * schema keeps it on the BALLOT, not the item, so it is the same rule for
     * every motion on the paper.
     *
     * **It is here so the paper can refuse an uncountable ballot before a member
     * marks it.** §15.2/§18.1 make voting by Houses MANDATORY under a
     * `summoned-per-house` base, and the engine refuses that combination
     * `by-houses-required` (fatal) rather than silently forcing it — so a
     * `summoned-per-house` motion sitting on a `one-tally` ballot is a ballot
     * that cannot be counted, discovered after every member has already voted.
     * Read together with {@link CastItem.majorityBase}, this is what lets the
     * screen say so first.
     *
     * A plain `string` on the wire, like `mode`, `itemKind` and `secrecy` above,
     * and never absent or defaulted: the refusal cannot be written against a
     * house rule the screen had to guess.
     */
    houseRule: string;
  };
  paper: {
    /** True when a claim already existed — a refreshed tab, a double-tap on
     *  hall wifi, or a second device. Reported so the surface can be honest,
     *  never so it can behave differently. Nothing renders it today. */
    resumed: boolean;
    /** Whether this elector's vote on this ballot has already been recorded,
     *  read from `participation`, which is Book One and public by design. A
     *  BOOLEAN and never the marks — see the note at the head of this section. */
    recorded: boolean;
    /**
     * The House STAMPED on this paper, which is what decides which tally the
     * vote lands in (§15.2 / SO 8.6). It is `voterHouseForPaper`'s answer and
     * **not an assertion about the person's roll row**: on a secret ballot the
     * Bishop votes on a `clergy`-stamped paper (registrar ruling 2026-08-20),
     * while on an attributable one the stamp is `bishop`. Three words,
     * therefore, not two — ../paper's `paperHouseLabel` is where they are named.
     */
    voterHouse: string;
  };
  /** EVERY item on the ballot, in agenda order, including the ones this House
   *  may not decide. See {@link CastItem.entitled}. */
  items: CastItem[];
}

/**
 * One item's decision, on the way to the server — `{ itemId, choice }` plus
 * `candidateId` iff the choice names one, which is `MarkSubmission` in
 * `@cca/decides-worker/src/cast.ts`.
 *
 * **There is no `paperId` here and there must never be one.** The route reads
 * three fields and ignores every other, and the paper is resolved through
 * `paper_claim` for this elector — which is what makes cross-elector mark
 * injection unexpressible rather than merely refused.
 *
 * **TWO VOCABULARIES, ONE ENVELOPE, AND THEY DO NOT MIX.** `recordCast` picks
 * the set of words a `choice` is tested against from the ITEM's own kind —
 * `MOTION_CHOICES` or `POSITION_CHOICES`, sharing `abstain` and `blank` and
 * nothing else — so an `aye` on an election is refused `item_not_markable` and
 * a `no-confidence` on a motion likewise. The union here is the browser's half
 * of that: each arm is a whole, valid body for the kind of item it names, and
 * the screen that builds it has already narrowed the item to one kind (../paper's
 * `MarkableItem` and `MarkableMotion`).
 */
export type CastMark = { itemId: string } & (PaperMark | MotionMark);

/**
 * What a successful submission answers, and it is the whole body: `recorded`
 * and nothing else. No receipt number, no position, no echo of the mark — the
 * confirmation confirms the ACT, and a payload that carried the content would be
 * one refactor away from a screen that prints it.
 *
 * It arrives as a **201**, which {@link apiFetch} passes through on `res.ok`
 * like any other 2xx. Nothing on this path may key on `=== 200`.
 */
export interface CastReceipt {
  recorded: true;
}

/**
 * THE ONE PLACE THE MARKS ENVELOPE IS BUILT, and it is a module function rather
 * than a method so that {@link api.returnBlankPaper} can call it without `api`
 * referring to itself inside its own initialiser. Both submissions this app can
 * make — a marked paper and a blank one — go through this line, so the contract
 * test that pins the body pins both.
 */
function postMarks(ballotId: string, marks: CastMark[]): Promise<CastReceipt> {
  return apiFetch<CastReceipt>(`/api/ballot/${encodeURIComponent(ballotId)}/marks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marks }),
  });
}

// ── The scrutineer surface ─────────────────────────────────────────────────
// close → seal → count → declare, the four administrative acts. Each POST re-gates
// server-side (SO 15.11) — this client asserts nothing about who may run them.
//
// AND, BEFORE ANY OF THEM, ONE READ. See {@link ResultsRead}: the panel asks the
// ballot what state it is at rather than assuming one.

/**
 * THE MARKS-ROLL CHECK as the worker sends it, on the results read and the count
 * response alike (`RollIntegrityFinding`, `@cca/decides-worker/src/marks-roll-audit.ts`):
 * how many people the public record of who voted on this ballot names whom the roll
 * says may not vote — an Officer, for example. A COUNT and never an identity.
 *
 * **TWO ARMS, and the second is why the type exists** (cca-5uma). `checked: true`
 * carries the count, and `0` there is the clean answer. `checked: false` is a check
 * that did not complete, and its count is `null` — never `0`, because a zero is the
 * clean answer and a failed check must never be read as one. No reason rides the
 * `false` arm: the worker keeps that on its own log.
 *
 * A WORKER OLDER THAN THIS BUNDLE sends `{ ineligibleOnRoll }` with no `checked`,
 * and its zero may be a failure it could not tell apart. The type does not model
 * that body; the screen's reading does — see `rollIntegrityFrom` (../results/view.ts,
 * the one reading, exported), which reads an absent `checked`, a `checked` that is
 * not the boolean `true`, and an absent or `null` object as not checked.
 */
export type RollIntegrity =
  { checked: true; ineligibleOnRoll: number } | { checked: false; ineligibleOnRoll: null };

/**
 * `GET /api/results/:ballotId` — the results sheet's own route, and the ONE cheap
 * read of a ballot's true state this SPA has.
 *
 * **IT ANSWERS FOR EVERY STATE, not only the three it will report.** A ballot in
 * 'closed', 'sealed' or 'declared' is served this body; one in 'draft', 'ready' or
 * 'open' is refused `409 { error: 'ballot_not_counted', state }` — which carries
 * the state just the same (GATE 4, `@cca/decides-worker/src/routes/results.ts`).
 * So the scrutineer panel reads the state off EITHER response class (see
 * {@link DecidesApiError.state}) and never has to guess at one. It admits admins
 * and scrutineers, so the operator who reached that panel is already served.
 *
 * **FIGURE-FREE**, as the route's own header sets out at length. `rollIntegrity`
 * is a COUNT of marks on this ballot's roll naming a subject who may not vote
 * ({@link RollIntegrity}), and `quorumDisclosure` puts the Administrator's declared
 * claim beside the effective-present roll; neither is the poll's arithmetic, which
 * exists only on the count response ({@link CountRunResult}). Both are declared
 * because the route SENDS them and a wire type that omits half a payload is a
 * description of the payload that is wrong. Neither is rendered off THIS read: the
 * results sheet draws `rollIntegrity` from the count response, where the sheet is.
 *
 * `state` and `mode` stay plain `string`s: they are CHECK-fenced `ballot` columns,
 * and this file does not dress a column off the wire as a checked union. The
 * scrutineer panel narrows `state` at the boundary that reads it.
 */
export interface ResultsRead {
  ballot: { id: string; title: string; state: string; mode: string };
  viewer: 'admin' | 'scrutineer';
  rollIntegrity: RollIntegrity;
  quorumDisclosure:
    | {
        house: RegisterHouse;
        declaredClaim: number;
        effectivePresent: number;
        diverges: boolean;
      }[]
    | null;
}

/**
 * One item's DISPLAY metadata on the counted sheet — the words the results sheet
 * needs that the arithmetic has no opinion about. `candidates` rides a POSITION
 * only (a motion carries none), and the three motion fields ride a MOTION only,
 * the mirror of it. `kind` is the ballot_item CHECK's word, a plain string on the
 * wire.
 */
export interface CountedSheetItem {
  id: string;
  title: string;
  kind: string;
  candidates?: { id: string; displayName: string }[];
  /**
   * THE INSTRUMENT — the words Synod agreed or refused, verbatim; **absent on a
   * position**, alongside the two names below.
   *
   * The same rule as on the paper the member marked ({@link CastItem.instrument}):
   * the results sheet renders it in full, never truncated and never behind a
   * "show more", because a reader who cannot read the whole motion cannot check
   * the report of it. A motion whose instrument does not arrive gets NO sheet
   * rather than a sheet of figures under a title — see `buildResultView`
   * (../scrutineer/ScrutineerPage.tsx).
   *
   * Prose an operator typed, and it reaches the DOM as the text child of a `<p>`,
   * never as markup. See `instrumentParagraphs` (../paper).
   */
  instrument?: string;
  /**
   * Who moved the motion, and who seconded it — display names as the record holds
   * them; **absent on a position**, and `null` on a motion whose record does not
   * carry the name (`ballot_item.mover` / `.seconder` are NULLable). An absent
   * name draws no row on the sheet rather than a fabricated attribution.
   *
   * Nothing here is an identity: the platform's identity is `Contacts.id`
   * (ADR-059), and these two are captions on a report.
   */
  mover?: string | null;
  seconder?: string | null;
}

/**
 * One recorded SO 15.5 extension of the poll — the instant the extend act landed
 * and the chain's own `reason` sentence VERBATIM (`BallotExtensionRecord`,
 * `@cca/decides-worker/src/ballot-events.ts`). The sentence is prose and is never
 * parsed here: it is the ONLY record of the closing time the electors were
 * originally told, because the extension overwrote both close columns, and
 * re-deriving instants out of it would be inventing a schema. `reason` is nullable
 * because the column is; nothing has ever written an extension without one.
 */
export interface BallotExtension {
  occurredAtUtc: string;
  reason: string | null;
}

/**
 * The display metadata the count route returns alongside the aggregate result, so
 * the scrutineer who ran the count renders the results sheet from this one
 * response. `mode` and `secrecy` are the ballot row's CHECK-fenced words — plain
 * strings on the wire, narrowed at the boundary that reads them (never asserted).
 *
 * Reconciled field by field on 2026-09-02 against the count route's response body
 * (`@cca/decides-worker/src/routes/scrutineer.ts`), the same discipline the cast
 * and setup sections record: nothing types the wire between the SPA and the
 * worker, so every key and every nullability below is the worker's own.
 *
 * **THE BOX, THE ROLL AND THE WINDOW** — the twelve keys after `resultDigest` are
 * what a scrutineer's signature is actually about ("I ran this count, on this box
 * of papers, against this roll"), returned so the sheet can state all three
 * instead of binding only the arithmetic. All of it is aggregate or the ballot's
 * own published schedule; nothing here is a paper or an elector.
 */
export interface CountedSheet {
  ballotName: string;
  secrecy: string;
  mode: string;
  engineVersion: number;
  /** The count instant as a UTC ISO string, formatted to NZ words by whoever
   *  assembles the `ResultView` — a browser that formats an instant must pick a
   *  zone, and the right one is Pacific/Auckland, never the browser's. */
  runAtUtc: string;
  definitionDigest: string;
  resultDigest: string;
  /** THE BOX — the seal's witness. A lawful seal of ZERO papers stamps 0, and 0
   *  is a real answer; null only on a ballot never sealed, which the count
   *  route's state gate has already refused, so a null here is deploy skew and
   *  nothing else. */
  papersSealedCount: number | null;
  papersSealedAtUtc: string | null;
  /** THE ROLL — the five pins `count_run` binds on every count: the frozen
   *  roll's fingerprint, the amendment sequence it was read at, and the summoned
   *  figure per House the count divided by. All five are null TOGETHER on a
   *  sitting with no frozen roll (a rehearsal before the freeze); null means NO
   *  FROZEN ROLL and never zero. */
  rollDigest: string | null;
  rollAmendmentSeq: number | null;
  summonedClergy: number | null;
  summonedLaity: number | null;
  summonedBishop: number | null;
  /** THE WINDOW AS IT STANDS — both halves of both pairs: the registrar's
   *  zoneless NZ wall clock (sixteen characters, see {@link BallotSchedule}) and
   *  the UTC instant the worker derived from it. A null bound is UNBOUNDED on
   *  that side (a `manual` ballot), not a gap in the record. */
  opensAtNz: string | null;
  opensAtUtc: string | null;
  closesAtNz: string | null;
  closesAtUtc: string | null;
  /** The acts that MOVED the close, oldest first — the four fields above are
   *  only the close as it finally stood. ALWAYS an array: `[]` is the answer
   *  "the poll ran to its published close", never null-as-absent, so a reader
   *  cannot render an extended poll and an unextended one down one branch. */
  extensions: BallotExtension[];
  items: CountedSheetItem[];
}

/**
 * `POST /api/ballot/:ballotId/count` — to the SCRUTINEER who ran it (SO 15.11).
 * The run id and status PLUS the aggregate `result` and the `sheet` metadata.
 * AGGREGATES ONLY: `result` is the engine's `CountResult` (tallies, scopes,
 * issues), never the raw vote list and never a paper or elector identity.
 *
 * `rollIntegrity` is the marks-roll check the route runs beside every count
 * ({@link RollIntegrity}) — declared here because the results sheet renders it.
 * The route also sends `quorumDisclosure`, which this type does not yet declare.
 */
export interface CountRunResult {
  countRunId: string;
  status: CountResult['status'];
  result: CountResult;
  rollIntegrity: RollIntegrity;
  sheet: CountedSheet;
}

/**
 * One signature standing against a results sheet, as a colleague's screen shows
 * it: WHO and WHEN, and nothing else. The worker never sends the signer's email,
 * IP, fingerprint or attestation wording on this read
 * (`ResultSignatureRecord`, `@cca/decides-worker/src/result-signature.ts`), and
 * this type declares none of them so no screen here can grow a column for one.
 * `acceptedAt` is a UTC instant, formatted to NZ words by the surface that
 * renders it.
 */
export interface ResultSignatureRecord {
  signerName: string;
  acceptedAt: string;
}

/** What `POST …/sign-result` answers: the audit row's id and instant, plus the
 *  FULL signature list including the one just given — so the panel redraws from
 *  the answer rather than appending optimistically to what it last read. */
export interface ResultSigned {
  ok: true;
  auditId: string;
  acceptedAt: string;
  signatures: ResultSignatureRecord[];
}

/** What `GET …/result-signatures/:countRunId` answers. An EMPTY list is the
 *  answer "nobody has signed this run", and it is a courtesy, never a fence: the
 *  worker's lookup is keyed on a recomputed fingerprint and says so in terms —
 *  nothing may treat this list's emptiness as authority to refuse or permit. */
export interface ResultSignaturesRead {
  countRunId: string;
  signatures: ResultSignatureRecord[];
}

// ── The operator setup surface ─────────────────────────────────────────────
// Where an Admin builds electoral structure: load and freeze the roll, and take a
// draft ballot through ready → open, or abandon it. ADMIN ONLY, decided
// server-side on every call (`src/routes/setup.ts`) — this client asserts nothing
// about who may run these acts.
//
// ── EVERY FIELD IS THE WORKER'S OWN WORD, SPELT ITS WAY ────────────────────
// Reconciled field by field on 2026-08-29 against `@cca/decides-worker`'s
// `src/routes/setup.ts`, `src/roll-load.ts` and `src/ballot-create.ts` — the same
// discipline the cast section above records, and for the same reason: nothing in
// this estate types the wire between the SPA and the worker, so a renamed field
// type-checks green on both sides and fails only at a synod sitting. `house` is
// `elector.house`'s three literals (reused as {@link RegisterHouse}); the ballot
// `state` and `itemKind` are plain `ballot`-column strings, narrowed by the
// surface that reads them, never asserted here.

/**
 * A ballot's VOTING WINDOW AS THE SERVER HOLDS IT — the `schedule` object on a
 * `GET /api/setup/:electionId` row, and the echo a successful
 * `POST …/schedule` answers with. **A READ shape, and the mirror of the
 * {@link BallotSchedule} WRITE shape below**: the two are deliberately not one
 * type, because this one carries the two derived instants and a request never may.
 *
 * **THE FOUR TIME FIELDS COME IN PAIRS, AND BOTH HALVES MATTER.** `opensAtNz` is
 * the registrar's own words — zoneless NZ wall clock, stored verbatim — and
 * `opensAtUtc` is the instant the WORKER derived from it, which is what
 * `withinWindow` actually enforces when a member tries to vote. A surface that
 * showed only the wall clock could not show the two disagreeing, and an hour's
 * silent DST error on a ballot that opens and closes by the clock is exactly the
 * failure this pair exists to make visible. {@link SchedulePanel} therefore renders
 * the instant beside the wall clock, quietly — **and never computes it**, because
 * the machine running this browser may be nowhere near Auckland.
 *
 * Under `manual` all four are `null`. Under `scheduled` either bound may be `null`,
 * which means UNBOUNDED on that side and is a lawful window, not a gap.
 *
 * `mode` stays a plain `string`: it is `ballot.schedule_mode`, a CHECK-fenced TEXT
 * column, and this file does not dress a column off the wire as a checked union
 * (see the note at the head of the file).
 */
export interface BallotScheduleView {
  mode: string;
  opensAtNz: string | null;
  opensAtUtc: string | null;
  closesAtNz: string | null;
  closesAtUtc: string | null;
}

/** One ballot on a sitting's setup surface, from `GET /api/setup/:id`.
 *  **FIGURE-FREE BY ITS COLUMN LIST** — the route selects no poll figure at all
 *  (`src/routes/setup.ts`), so there is no tally, turnout or count here to leak
 *  whatever state the row is in. That is the invariant, and it is the one worth
 *  stating: "a draft has no votes" was the old reason and it stopped being true the
 *  day this list widened to carry OPEN ballots, which do have votes. What protects
 *  the secrecy is the SELECT, not the state. `definitionDigest` is a fingerprint,
 *  not a figure. `state` is `draft` | `ready` | `open` (setup lists no other — an
 *  abandoned or closed ballot drops off), `itemKind` is `motion` | `position`. */
export interface DraftBallot {
  id: string;
  itemKind: string;
  title: string;
  state: string;
  definitionDigest: string | null;
  createdAtUtc: string;
  /**
   * This ballot's recorded voting window.
   *
   * **OPTIONAL FOR THE SAME REASON {@link Identity.electorSittings} IS, and for no
   * other**: it is a new field on the route, this SPA and the worker deploy
   * separately, and a bundle can meet a worker that predates it. Absent therefore
   * reads as "this server does not answer that question" — {@link SchedulePanel}
   * says so in one line and still saves — rather than as `undefined.mode` thrown in
   * the middle of the ballot list. The worker declares it REQUIRED and always sends
   * it (`src/routes/setup.ts`), so the absent case is deploy skew and nothing else.
   *
   * **It is READ ON EVERY LOAD, not held in component state.** A window kept only
   * in the panel that set it vanishes on the next refresh, which would leave a
   * registrar looking at an empty form and a ballot that opens on a clock.
   */
  schedule?: BallotScheduleView;
}

/** `GET /api/setup/:electionId` — this sitting's draft ballots, plus which
 *  sitting they belong to. `viewer` is always `'admin'`: the route refuses
 *  anybody who is not one before it answers. */
export interface DraftList {
  sitting: { electionId: string; mode: string; name: string };
  ballots: DraftBallot[];
  viewer: 'admin';
}

/** `POST /api/setup/:electionId/roll` — the roll AS INGESTED. `loaded` is how many
 *  elector rows the submitted roster REPLACED this sitting's roll with; `rollDigest`
 *  is the fingerprint the freeze will publish if nothing changes. A count of rows
 *  written is not a poll figure — no vote exists yet. */
export interface RollLoaded {
  loaded: number;
  rollDigest: string;
}

/** `POST /api/setup/:electionId/roll/freeze` — the one snapshot that fixes the
 *  roll. `rollDigest` is the published genesis of the amendment and attendance
 *  chains; a scrutineer who holds it can check the register begins where the roll
 *  ended. */
export interface RollFrozen {
  snapshotId: string;
  rollDigest: string;
}

/**
 * The quorum declaration on the OPEN body — `POST /api/setup/ballot/:id/open`.
 *
 * **THREE PER-HOUSE INTEGERS AND NO TOTAL** (registrar ruling 2026-08-16 / Steve
 * 2026-08-29): SO 12.1 is a per-House test, and a sum is satisfied by one House
 * carrying the room. `clergy` ≥ 0 and `laity` ≥ 0 — a House with nobody present
 * is the lawful, honest way to record an inquorate House — and `bishop` is 0 or 1,
 * because a diocese has one Bishop or none. The other three declaration columns
 * (the actor and the two receipts) are set SERVER-SIDE from the session and the
 * clock, never from this body. **The field names are the worker's:** the wire
 * carries `clergy` / `laity` / `bishop`, NOT `quorum_claimed_*` — those are the
 * column names the route maps them onto.
 */
export interface QuorumDeclaration {
  clergy: number;
  laity: number;
  bishop: number;
}

/**
 * A ballot's VOTING WINDOW — the body of `POST /api/setup/ballot/:ballotId/schedule`.
 *
 * The registrar has settled that a ballot carries ONE motion or ONE position, so a
 * ballot's window is that item's own voting window and there is nothing per-item to
 * set. `manual` is the default and today's behaviour: the ballot opens on the
 * Administrator's act and closes on a Scrutineer's, and no clock is consulted.
 *
 * **THE TWO BOUNDS ARE ZONELESS NZ WALL CLOCK, AND THAT IS THE CONTRACT, NOT A
 * CONVENIENCE.** Each is exactly sixteen characters, `YYYY-MM-DDTHH:MM`, carrying no
 * offset and no `Z` — the registrar's own words for a time in the hall. **The worker
 * derives the UTC instants; this browser must never do that arithmetic**, because the
 * machine it runs on may be nowhere near Auckland and its local zone is evidence about
 * nothing (House Rules — NZ time everywhere). {@link SchedulePanel} therefore takes
 * these straight from an `<input type="datetime-local">`, whose value is already
 * exactly this shape, and passes them through untouched.
 *
 * **EITHER BOUND MAY BE `null`, AND `null` IS AN ANSWER RATHER THAN A GAP** —
 * "unbounded on that side". A `scheduled` ballot with no `opensAtNz` is opened by
 * hand and then closes on the clock; one with no `closesAtNz` opens on the clock and
 * stays open until a Scrutineer closes it. Both are lawful windows and neither is a
 * validation failure, which is why the fields are nullable rather than optional: an
 * ABSENT key would be indistinguishable from a client that failed to send one.
 *
 * **A WELL-FORMED WALL CLOCK CAN STILL NAME A TIME THAT NEVER HAPPENS**, and the
 * worker answers `schedule_time_does_not_exist` (422) when it does: New Zealand
 * skips 02:00–02:59 entering daylight saving, so a request for 2:30am that morning
 * is a real string for an unreal instant. It is a SEPARATE word from
 * `schedule_invalid` because the repair is different, and this browser cannot
 * pre-empt it — knowing which hour vanished is timezone arithmetic, which is the
 * worker's to do. See {@link BallotScheduleView} for what comes back.
 */
export type BallotSchedule =
  { mode: 'manual' } | { mode: 'scheduled'; opensAtNz: string | null; closesAtNz: string | null };

/**
 * What `POST /api/setup/ballot/:ballotId/extend` answers on a 200 — the window as
 * it NOW STANDS on the row, never an echo of what was asked for.
 *
 * **`title` AND `closesAtNz` ARE THE REPORT, AND THAT IS THE POINT OF CARRYING
 * THEM.** Extending the wrong ballot is silent and cannot be undone, and two
 * instants alone cannot tell an operator that she moved the motion instead of the
 * election — so the screen announces the SERVER's title and the SERVER's wall clock
 * rather than what it believed it pressed. The route derives `closesAtNz` from the
 * stored instant on every one of its three 200 exits, including the two idempotent
 * ones where the close that stands is somebody else's.
 *
 * `previousClosesAtUtc` is the instant the close moved FROM. Once the UPDATE lands
 * that value exists nowhere else at all — not on the row, which now holds the new
 * one — so it is reported here and written onto the ballot's chain. It is nullable
 * for deploy skew alone: the route always sends it, and gate 3 has already refused
 * an unbounded poll by the time any 200 is reachable.
 */
export interface ExtendedWindow {
  ok: true;
  ballotId: string;
  title: string;
  closesAtUtc: string;
  closesAtNz: string;
  previousClosesAtUtc: string | null;
}

/**
 * One elector on a roster handed to `POST /api/setup/:electionId/roll`, spelt the
 * worker's way (`src/roll-load.ts`'s `validateRoster`). `contactId` is the Zoho
 * `Contacts.id` (this platform's identity, never the address — ADR-059); `email`
 * must arrive normalised (lowercase, trimmed) and address-shaped; `house` is one of
 * the three roll literals; `mayVote` defaults TRUE and `false` marks an Officer,
 * who stays on the roll and in a House but outside the voting base.
 */
export interface RosterElector {
  contactId: string;
  email: string;
  house: RegisterHouse;
  mayVote?: boolean;
}

/**
 * `GET /api/roster/pull` — the Zoho pull that becomes the roster fed to
 * {@link api.loadRoll}.
 *
 * **THE ENDPOINT IS NOT BUILT YET (P3b).** It is declared here because the roll
 * panel calls it and a wire type that omits half a flow is a description of the
 * flow that is wrong — but a call today answers `404`, which the panel reads as
 * "not yet available" rather than as an error. The follow-on that builds the Zoho
 * binding owns the real shape; this is the smallest one its consumer needs.
 */
export interface RosterPull {
  electors: RosterElector[];
}

// ── Building a ballot ──────────────────────────────────────────────────────
// The request body `POST /api/setup/:electionId/ballot` builds a ballot from —
// mirrored FIELD FOR FIELD from `@cca/decides-worker`'s `src/ballot-create.ts`
// (`CreateBallotSpec` / `BallotItemInput` / `BallotCandidateInput`), the same
// wire-typing discipline the setup section above records. The builder assembles this
// from the operator's working draft in `src/setup/spec.ts`; `createBallot` validates
// it server-side and answers a structured refusal this SPA renders through
// `src/setup/copy.ts`. The unions are the worker's; where a field is a plain column
// the DATABASE fences (`motionClass`, `majorityBase`) it stays `string` on the wire.

/** One item on the ballot. The motion columns and the position columns are mutually
 *  exclusive — the worker refuses a row that mixes them (`position_has_motion_columns`
 *  / `motion_has_position_columns`), and the builder never sends one. */
export interface BallotItemInput {
  id: string;
  kind: 'motion' | 'position';
  agendaPosition: number;
  title: string;
  // ── motion columns (absent/null on a position) ──
  motionClass?: string | null;
  thresholdKind?: 'simple' | 'super' | null;
  thresholdNumerator?: number | null;
  thresholdDenominator?: number | null;
  majorityBase?: string | null;
  /** THE OPERATIVE WORDS — what Synod is asked to resolve, as against `title`,
   *  which is how the motion appears on the agenda. REQUIRED on a motion (the
   *  worker answers `instrument_required`) and optional here for the same reason
   *  `majorityBase` is: it must be ABSENT on a position, which the worker refuses
   *  as `position_has_motion_columns`. Deliberately unbounded — an amendment runs
   *  to paragraphs. */
  instrument?: string | null;
  /** The mover, where one is recorded — genuinely optional, and `null` is the
   *  absence. An empty string is a VALUE, and the worker refuses one as
   *  `mover_invalid`; the builder sends `null` for a blank field. */
  mover?: string | null;
  /** The seconder, where one is recorded. Optional and nullable exactly as
   *  `mover` — refused as `seconder_invalid` when present but not a usable name. */
  seconder?: string | null;
  // ── position columns (absent/null on a motion) ──
  electorate?: 'clergy' | 'laity' | 'both' | null;
  seats?: number | null;
  alternateSeats?: number | null;
  method?: 'fptp' | 'approval' | null;
}

/** One nomination on a position item. `contactId` is the nominee's Zoho
 *  `Contacts.id` where there is one; the builder sends `null` (the Zoho pick is a
 *  later phase, P3b). There is NO 'No Confidence' nominee — it is a mark choice, never
 *  a row. */
export interface BallotCandidateInput {
  id: string;
  itemId: string;
  displayName: string;
  contactId?: string | null;
}

/** The whole create request. `secrecy` is honoured only for a MOTION ballot; a
 *  POSITION ballot is always written 'secret' (SO 15.9), so the builder omits it
 *  there rather than sending a value the server would ignore. */
export interface CreateBallotSpec {
  title: string;
  itemKind: 'motion' | 'position';
  houseRule: 'one-tally' | 'separate-chambers';
  secrecy?: 'secret' | 'attributable';
  items: BallotItemInput[];
  candidates: BallotCandidateInput[];
}

/** What a successful create answers — `201 { id, digest }`. `id` is the SERVER-MINTED
 *  ballot id (the spec's item and candidate ids are the caller's); `digest` is the
 *  ballot's definition fingerprint. Spelt the ROUTE's way: `src/routes/setup.ts`
 *  returns `{ id: result.ballotId, digest: result.digest }`, so `id` is the ballot id
 *  and not an item's. */
export interface CreateBallotResult {
  id: string;
  digest: string;
}

export const api = {
  /** Who the server says you are. The role is NOT cached anywhere: this is
   *  re-asked, and re-resolved server-side, every time the shell loads. */
  me: () => apiFetch<Identity>('/api/auth/me'),

  /** The ballots open to this member. Standing comes from the roll, re-resolved
   *  on the request that asks — so a ballot closed, or a member stood down,
   *  between two loads is simply not in the second answer. */
  myBallots: () => apiFetch<{ ballots: OpenBallot[] }>('/api/me/ballots'),

  /**
   * Issue-or-resume this member's paper for one ballot.
   *
   * A POST because it MINTS something the first time — a paper and a claim — and
   * a GET that writes is a GET a proxy may replay. It is nevertheless safe to
   * repeat: the claim makes the second call a resume, which is what carries a
   * refreshed tab through a bad moment of hall wifi.
   *
   * **This is one of the two moments of network exposure in the whole flow**,
   * and it is deliberately at the START of it: the paper is fetched when the
   * item opens so that marking is free, silent and local, and the only other
   * request is the submit.
   */
  issuePaper: (ballotId: string) =>
    apiFetch<CastPaperView>(`/api/ballot/${encodeURIComponent(ballotId)}/paper`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /**
   * Record the paper — **the whole paper, once.**
   *
   * `mark` is append-only and its DELETE is fenced, so there is no edit and no
   * un-mark: a partly-submitted paper would be permanent. All or nothing, one
   * request, one batch server-side.
   *
   * A `409 already_voted` is not an error to this client; it is an answer, and
   * the screen says so calmly.
   *
   * **The envelope is `{ marks }`, and the name is not interchangeable with
   * `{ items }`.** `readMarks` (`@cca/decides-worker/src/routes/ballot.ts`)
   * answers `400 bad_request` to a body whose `marks` is not an array — and
   * deliberately does NOT read an absent `marks` as an empty paper, because
   * `marks: []` is a lawful blank paper and a body with no `marks` key is a
   * client that failed to send one. Under the wrong name every submission from
   * this screen was the second read as the first.
   */
  recordMarks: postMarks,

  /**
   * **RETURN THE PAPER BLANK** — a deliberate act, and a lawful one (registrar
   * ruling 2026-08-27).
   *
   * On the wire it is `{ marks: [] }`, which is the SERVER's model of a blank
   * paper rather than a convention invented here: `recordCast` writes one
   * `choice = 'blank'` row for every ENTITLED item the body carried no
   * submission for, and no row at all for the rest — so an empty array blanks
   * exactly the items that are this elector's, computed from the same read the
   * rows are built from. The alternative — sending `{ choice: 'blank' }` per
   * item — would make this browser enumerate the entitled set a second time and
   * earn `422 item_not_markable` the moment its answer disagreed with the
   * roll's. `packages/cca-decides-worker/test-integration/cast-path.test.ts:713`
   * pins the empty-array behaviour end to end.
   *
   * **IT EXISTS SO THAT `recordMarks(id, [])` NEVER APPEARS AT A CALL SITE.** A
   * bare empty array is one deleted character away from an accidental blank
   * cast, and a blank cast cannot be undone — `mark` is append-only and its
   * DELETE is fenced. A named method makes the act legible, greppable, and
   * impossible to reach by dropping something. It is a thin wrapper on purpose:
   * one envelope, built in one place ({@link postMarks}), so the contract test
   * that pins `recordMarks` pins this too.
   */
  returnBlankPaper: (ballotId: string) => postMarks(ballotId, []),

  /** The attendance register for one sitting. ADMIN ONLY, decided server-side —
   *  a scrutineer is refused `not_administrator`. Re-fetched on every view, and
   *  never cached: a member stood down after being ticked must drop off the list
   *  before the next quorum declaration is made. */
  register: (electionId: string) =>
    apiFetch<RegisterView>(`/api/register/${encodeURIComponent(electionId)}`),

  /**
   * Append ONE attendance event.
   *
   * A CORRECTION USES THIS SAME CALL. There is no update: the register is
   * append-only, so marking a person absent who was recorded present is one more
   * POST, and both rows stay legible to a scrutineer.
   */
  recordPresence: (
    electionId: string,
    body: { electorId: string; presence: Presence; reason?: string },
  ) =>
    apiFetch<RegisterAppended>(`/api/register/${encodeURIComponent(electionId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /**
   * READ where this ballot stands — {@link ResultsRead}, and the read the
   * scrutineer panel makes before it offers anything.
   *
   * A GET, and it writes nothing, so it is safe on every load and on a retry. It
   * is the ONLY call on this surface that is not an act: the state it answers is
   * a belief, never an authorisation, and the four acts below each re-gate on the
   * state the row holds at the moment they run.
   *
   * **A 409 IS AN ANSWER HERE, NOT A FAILURE.** A ballot that has not been closed
   * is refused `ballot_not_counted` with its state on the body, which is what
   * lets a panel tell a draft from an open poll. The caller reads
   * {@link DecidesApiError.state} on the throw.
   */
  results: (ballotId: string) =>
    apiFetch<ResultsRead>(`/api/results/${encodeURIComponent(ballotId)}`),

  /**
   * CLOSE the poll — open → closed. Scrutineer only, decided server-side; a
   * closed ballot no longer accepts votes. Body is `'{}'`, a deliberate POST, and
   * the route reads nothing from it: the ballot and its mode come from the row,
   * never the request.
   */
  close: (ballotId: string) =>
    apiFetch<{ ok: true }>(`/api/ballot/${encodeURIComponent(ballotId)}/close`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /** SEAL the papers — closed → sealed, the severance that unlinks each paper
   *  from the elector who cast it. Scrutineer only. Cannot be undone. */
  seal: (ballotId: string) =>
    apiFetch<{ ok: true }>(`/api/ballot/${encodeURIComponent(ballotId)}/seal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /**
   * COUNT the sealed papers. Scrutineer only. Returns the aggregate result and
   * the sheet metadata ({@link CountRunResult}) — the source the results sheet is
   * rendered from. Re-runnable: `count_run` is append-only, so a second count is
   * added beside the first, never a replacement, and this call is safe to repeat.
   */
  count: (ballotId: string) =>
    apiFetch<CountRunResult>(`/api/ballot/${encodeURIComponent(ballotId)}/count`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /**
   * DECLARE the result — record the President's act (SO 14.1(k)). Scrutineer OR
   * admin. `president` is the recorded NAME of the President who declared;
   * `countRunId` names WHICH count is being declared (from the count step). The
   * server sets who typed it, and the instant. A count that is not declarable —
   * fatal, or not this ballot's — is refused by the declaration triad.
   */
  declare: (ballotId: string, opts: { president: string; countRunId: string }) =>
    apiFetch<{ ok: true }>(`/api/ballot/${encodeURIComponent(ballotId)}/declare`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        declared_president: opts.president,
        declared_count_run_id: opts.countRunId,
      }),
    }),

  /**
   * SIGN the results sheet — the scrutineer's own attestation to ONE count
   * (SO 15.11; scrutineer only, NARROWER than declare, decided server-side).
   * `signerName` and `signatureText` are stored VERBATIM and printed, so the
   * caller sends exactly the sentence it showed; the signer's ADDRESS is never a
   * body field — the worker takes it from the session. Lawful more than once (a
   * synod has more than one scrutineer), and cannot be withdrawn: `e_sig_audit`
   * is append-only. A refused body is `422 missing_signature`; a run id that is
   * not this ballot's is `422 unknown_count_run`.
   */
  signResult: (
    ballotId: string,
    opts: { countRunId: string; signerName: string; signatureText: string },
  ) =>
    apiFetch<ResultSigned>(`/api/ballot/${encodeURIComponent(ballotId)}/sign-result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        countRunId: opts.countRunId,
        signerName: opts.signerName,
        signatureText: opts.signatureText,
      }),
    }),

  /**
   * WHO HAS ALREADY SIGNED one count run — name and instant only, scrutineer
   * only. Read before the signing form is offered, so nobody signs twice by
   * accident; never a gate, because a second signature is lawful and the
   * worker's own header forbids building refusal or permission on this list. An
   * unknown or foreign run is `404 unknown_count_run`, distinct from the
   * ballot's own 404.
   */
  resultSignatures: (ballotId: string, countRunId: string) =>
    apiFetch<ResultSignaturesRead>(
      `/api/ballot/${encodeURIComponent(ballotId)}/result-signatures/${encodeURIComponent(countRunId)}`,
    ),

  // ── The operator setup surface ────────────────────────────────────────────
  // ADMIN ONLY, re-resolved server-side on every call. See the type section above
  // for the wire shapes and the field-by-field reconciliation note.

  /**
   * PULL the roll from Zoho — **NOT BUILT YET (P3b).** A call today answers `404`,
   * which the roll panel reads as "not yet available". It is the step that would
   * FEED {@link loadRoll} its roster, kept here so the panel that owns that flow
   * can express it. `electionId` rides as a query parameter because the pull is
   * scoped to one sitting; the follow-on that wires Zoho owns the real contract.
   */
  rosterPull: (electionId: string) =>
    apiFetch<RosterPull>(`/api/roster/pull?electionId=${encodeURIComponent(electionId)}`),

  /**
   * LOAD (or REPLACE) the roll — pre-freeze working state. A re-post REPLACES this
   * sitting's elector rows wholesale (the roll is built and rebuilt freely right up
   * to the freeze — `0003_roll.sql:1940`), so the caller is told the count it
   * replaced. The mode is the SITTING's, never this request's. Refused
   * `already_frozen` once the roll is fixed.
   */
  loadRoll: (electionId: string, electors: RosterElector[]) =>
    apiFetch<RollLoaded>(`/api/setup/${encodeURIComponent(electionId)}/roll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ electors }),
    }),

  /**
   * FREEZE the roll — the one-way act that fixes it as the `roll_snapshot` and
   * publishes its digest. **A point of no return** (Sexton's law): after it the
   * roll changes only on the amendment path, and the attendance register becomes
   * available. Body is `'{}'` — the route reads nothing from it; `frozen_by` is the
   * signed-in Administrator, resolved server-side, never a body field.
   */
  freezeRoll: (electionId: string) =>
    apiFetch<RollFrozen>(`/api/setup/${encodeURIComponent(electionId)}/roll/freeze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /** This sitting's DRAFT ballots, and which sitting they belong to. Only draft
   *  and ready rows: a published ballot is the scrutineer surface's concern. */
  listDrafts: (electionId: string) =>
    apiFetch<DraftList>(`/api/setup/${encodeURIComponent(electionId)}`),

  /**
   * BUILD a ballot in 'draft' — the operator's create act. The body is the whole
   * {@link CreateBallotSpec}, assembled by `src/setup/spec.ts` from the working
   * draft. `createBallot` validates it server-side and answers `422` with a
   * structured word for a spec it refuses, or `409` for a caller item/candidate id
   * already taken (both rendered by `src/setup/copy.ts`). A draft is REVERSIBLE — it
   * can be abandoned — so this is NOT a point of no return.
   */
  createBallot: (electionId: string, spec: CreateBallotSpec) =>
    apiFetch<CreateBallotResult>(`/api/setup/${encodeURIComponent(electionId)}/ballot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(spec),
    }),

  /** MARK a draft READY — draft → ready, recording that the Administrator has
   *  finished building it. Rebuildable state up to here; nothing binds. Body is
   *  `'{}'`. A re-run on a ballot that has moved on answers `wrong_state`. */
  markReady: (ballotId: string) =>
    apiFetch<{ ok: true }>(`/api/setup/ballot/${encodeURIComponent(ballotId)}/ready`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),

  /**
   * SET this ballot's VOTING WINDOW — `manual` (the default, and today's behaviour)
   * or `scheduled` with an optional opening time and an optional closing time. See
   * {@link BallotSchedule} for the wall-clock contract and for why either bound may
   * lawfully be `null`.
   *
   * **NOT a point of no return, and NOT one-way**: the window is working state on a
   * `draft` or `ready` ballot and may be set, moved or dropped back to `manual` as
   * often as the operator likes. It stops being editable through this route the
   * moment the ballot opens, which the worker answers `409 schedule_not_editable`
   * (a CONFLICT with the ballot's state, not a malformed request — the three
   * time-shape refusals `schedule_invalid`, `schedule_time_does_not_exist` and
   * `schedule_window_inverted` are the 422s) — moving the clock on a poll people
   * are voting in is a
   * different act with a different fence, and this route is not it.
   *
   * The body is built field by field rather than by stringifying the argument, the
   * same way {@link openBallot} builds its declaration: the route reads exactly these
   * keys, and a body that carried an extra one would be this client asserting
   * something about a column it does not own.
   *
   * **THE ANSWER CARRIES THE STORED WINDOW, AND IT IS THE RECORD RATHER THAN AN
   * ECHO OF THE REQUEST.** The route reads back the five values it wrote, including
   * the two UTC instants IT derived — so the panel renders what the row now holds,
   * never a hopeful copy of what was typed. That is what lets an operator see which
   * daylight-saving offset was applied before anybody votes.
   */
  setBallotSchedule: (ballotId: string, schedule: BallotSchedule) =>
    apiFetch<{ ok: true; schedule: BallotScheduleView }>(
      `/api/setup/ballot/${encodeURIComponent(ballotId)}/schedule`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          schedule.mode === 'manual'
            ? { mode: 'manual' }
            : {
                mode: 'scheduled',
                opensAtNz: schedule.opensAtNz,
                closesAtNz: schedule.closesAtNz,
              },
        ),
      },
    ),

  /**
   * MOVE THIS POLL'S CLOSE LATER — `POST …/extend`, and the ONE act that changes a
   * running poll's window. `/schedule` above stops working the moment a ballot
   * opens; this route takes over, and it is deliberately narrower.
   *
   * **IT ONLY EVER WIDENS.** The worker refuses an earlier time, an equal-or-past
   * one, a poll that is not open, a poll with no close to move, and a move further
   * than twelve hours in one step — six words, one per gate. An EXACT repeat of a
   * request that already landed is a RETRY and answers 200 with the window that
   * stands, so a reply lost on hall wifi does not become a second extension.
   *
   * **EVERY REFUSAL CARRIES THE FIGURE THAT BEATS IT** — the close that stands, the
   * latest instant that would be accepted, the cap in hours, the shape and an
   * example. They arrive on {@link DecidesApiError.details} and `setup/ExtendStep.tsx`
   * renders them, because a registrar in a hall cannot compute any of them from a
   * bare token. None is a secret: a ballot's window is its own published schedule.
   *
   * `closesAtNz` is the same zoneless NZ wall clock {@link BallotSchedule} carries
   * and travels under the identical contract — sixteen characters, no offset, no
   * `Z`, taken straight from a `datetime-local` input, and **this browser never
   * derives the instant**.
   */
  extendBallot: (ballotId: string, closesAtNz: string) =>
    apiFetch<ExtendedWindow>(`/api/setup/ballot/${encodeURIComponent(ballotId)}/extend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Built field by field, the same rule `setBallotSchedule` and `openBallot`
      // keep: the route reads exactly this one key, and a body carrying another
      // would be this client asserting something about a column it does not own.
      body: JSON.stringify({ closesAtNz }),
    }),

  /**
   * OPEN a ballot — ready → open, THE ENLIVENING. **A point of no return**: opening
   * starts the binding vote. The body is the {@link QuorumDeclaration}, three
   * per-House integers and **no total** — `{ clergy, laity, bishop }`, spelt the
   * worker's way and never as `quorum_claimed_*`. The numbers are the
   * Administrator's DECLARED count of who they see in the room; they are recorded,
   * not recomputed (registrar 2026-08-16).
   */
  openBallot: (ballotId: string, quorum: QuorumDeclaration) =>
    apiFetch<{ ok: true }>(`/api/setup/ballot/${encodeURIComponent(ballotId)}/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clergy: quorum.clergy,
        laity: quorum.laity,
        bishop: quorum.bishop,
      }),
    }),

  /**
   * ABANDON a draft or ready ballot — the ONE correction a ballot that has left
   * draft has (it cannot walk back). `reason` is required, 1..500 characters, and
   * is the only body field — `abandoned_by` is the signed-in Administrator, set
   * server-side. A ballot that has polled is closed, never abandoned, and answers
   * `wrong_state`.
   */
  abandonBallot: (ballotId: string, reason: string) =>
    apiFetch<{ ok: true }>(`/api/setup/ballot/${encodeURIComponent(ballotId)}/abandon`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }),

  /** End this device's session. Body is `'{}'` so the request has a content type
   *  and reads as a deliberate POST. */
  logout: () =>
    apiFetch<{ ok: boolean }>('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    }),
};
